import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { GearSolidV6, PrintSolidV6, WandMagicSparklesSolidV6 } from "@shared/v2/Icomoon";
import Dropdown from "@shared/v2/Dropdown";
import IconButton from "@shared/v2/IconButton";
import TextButton from "@shared/v2/TextButton";
import Typography from "@shared/v2/Typography";
import HeadingSelect from "./HeadingSelect";
import Section from "./Section";
import TeamBusinessGoals from "./TeamBusinessGoals";
import DefaultableGoals from "./DefaultableGoals";
import { Menu, Option, SingleValue } from "./CustomDropdownComponents";
import TeamActivityGoals from "./TeamActivityGoals";
import GoalSettingsModal from "./GoalSettingsModal/GoalSettingsModal";
import TeamProductionWizardModal from "./TeamProductionWizardModal";
import BusinessPlanSheet from "./TeamProductionWizardModal/BusinessPlanSheet";
import { useCurrentUser } from "../../reducers/layoutReducer/selectors";
import SetGoalsBanner, { getTargetYear } from "./SetGoalsBanner";
import useWizard from "../services/useWizard";
import { WizardProvider } from "./TeamProductionWizardModal/WizardContext";

const ReportingGoals = ({ agents }) => {
  const { isAdmin } = useCurrentUser().account;
  const params = new URLSearchParams(window.location.search);
  const userUuid = params.get("user_uuid");
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [goalSettingsIsOpen, setGoalSettingsIsOpen] = useState(false);
  const [goalSettingsInitialTab, setGoalSettingsInitialTab] = useState(null);
  const [goalsRefreshKey, setGoalsRefreshKey] = useState(0);
  const [wizardIsOpen, setWizardIsOpen] = useState(false);
  const handleWizardClose = useCallback(() => setWizardIsOpen(false), []);
  const [year, setYear] = useState(params.get("year") || getTargetYear());
  const dropdownOptions = useMemo(
    () =>
      agents.map(({ id, full_name: fullName, avatar }) => ({
        value: id,
        label: fullName,
        meta: { avatar },
      })),
    [],
  );
  const [agent, setAgent] = useState(
    dropdownOptions?.find(({ value }) => value === userUuid) ||
      dropdownOptions?.find(({ value }) => value === null),
  );
  const wizardData = useWizard({ agentId: agent?.value, year });

  const openGoalSettings = useCallback((initialTab = null) => {
    setGoalSettingsInitialTab(initialTab);
    setGoalSettingsIsOpen(true);
  }, []);

  const handleGoalSettingsClose = useCallback(() => {
    setGoalSettingsIsOpen(false);
    setGoalSettingsInitialTab(null);
  }, []);

  const handleGoalSettingsSaved = useCallback(() => {
    setGoalsRefreshKey((current) => current + 1);
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams({ year });
    window.history.pushState(null, null, `${window.location.pathname}?${searchParams}`);
  }, [year]);

  return (
    <div className="tw-bg-neutral-gray-5 tw-p-[16px]">
      <div className="tw-mx-auto tw-max-w-[1320px] tw-w-full tw-flex tw-flex-col tw-gap-[24px]">
        <WizardProvider
          year={year}
          show={wizardIsOpen}
          onClose={handleWizardClose}
          wizardData={wizardData}
          agentId={agent?.value}
        >
          <SetGoalsBanner
            agent={agent}
            onSetGoals={(bannerYear) => {
              if (bannerYear) setYear(bannerYear);
              setWizardIsOpen(true);
            }}
          />
          <Section className="tw-flex tw-items-baseline tw-justify-between">
            <div className="tw-w-full tw-flex tw-justify-between tw-align-top tw-flex-wrap">
              <div className="tw-flex tw-flex-col tw-gap-[12px]">
                <div className="tw-flex tw-items-baseline">
                  <HeadingSelect
                    name="year"
                    title={<h2 className="tw-text-24d tw-m-0 tw-mr-[16px]">Goal Setting</h2>}
                    value={year}
                    onChange={setYear}
                  />

                  <div className="tw-flex tw-items-center tw-gap-[8px]">
                    {isAdmin && (
                      <IconButton size="small" schema="tertiary" onClick={() => openGoalSettings()}>
                        <GearSolidV6 />
                      </IconButton>
                    )}

                    <TextButton
                      onClick={() => setWizardIsOpen(true)}
                      className="tw-flex tw-items-center tw-gap-[4px]"
                    >
                      <WandMagicSparklesSolidV6 /> Help Me Set My Goals
                    </TextButton>

                    <TextButton
                      onClick={() => window.print()}
                      className="tw-flex tw-items-center tw-gap-[4px]"
                    >
                      <PrintSolidV6 /> Print My Goal Sheet
                    </TextButton>
                  </div>

                  <GoalSettingsModal
                    show={goalSettingsIsOpen}
                    year={year}
                    userUuid={agent?.value}
                    targetUserName={agent?.label}
                    initialTab={goalSettingsInitialTab}
                    onSaved={handleGoalSettingsSaved}
                    onClose={handleGoalSettingsClose}
                  />
                  <TeamProductionWizardModal />
                </div>
                <Typography className="!tw-text-gray-50">
                  Set business, production, and activity tracking goals for a period of time. View progress
                  towards goals on your dashboards.
                </Typography>
              </div>
              {isAdmin && (
                <div className="tw-flex tw-flex-col">
                  <Dropdown
                    menuIsOpen={menuIsOpen}
                    onMenuOpen={() => setMenuIsOpen(true)}
                    onMenuClose={() => setMenuIsOpen(false)}
                    components={{ Menu, Option, SingleValue }}
                    className="tw-min-w-[300px]"
                    label="Assign goals for:"
                    value={agent}
                    options={dropdownOptions}
                    onChange={setAgent}
                  />
                </div>
              )}
            </div>
          </Section>

          <div className="tw-flex tw-flex-wrap tw-items-start tw-gap-[16px]">
            <div className="tw-basis-[500px] tw-grow tw-flex tw-flex-col tw-gap-[16px]">
              <TeamBusinessGoals
                year={year}
                isAdmin={isAdmin}
                refreshKey={goalsRefreshKey}
                workingMonths={12}
              />
              <DefaultableGoals
                agent={agent}
                refresh={!wizardIsOpen}
                refreshKey={goalsRefreshKey}
                year={year}
                isAdmin={isAdmin}
                section="agent_production"
                workingMonths={12}
                title="Personal Production Goals"
                description={
                  agent?.value ? (
                    <div>
                      Set the <span className="tw-font-bold">production standards for {agent.label}</span> on
                      goals such as units sold, units listed, units pended, closed volume, and gross commision
                      income (GCI).
                    </div>
                  ) : (
                    "Set individual production goals for yourself based on units sold, units listed, units pended, closed volume, gross commission income (GCI). "
                  )
                }
              />
            </div>
            <div className="tw-basis-[500px] tw-grow tw-flex tw-flex-col tw-gap-[16px]">
              <TeamActivityGoals
                year={year}
                isAdmin={isAdmin}
                refreshKey={goalsRefreshKey}
                workingMonths={12}
              />
              <DefaultableGoals
                agent={agent}
                refresh={!wizardIsOpen}
                refreshKey={goalsRefreshKey}
                year={year}
                isAdmin={isAdmin}
                section="lead_interaction"
                workingMonths={12}
                title="Brivity Activity Goals"
                description={
                  agent?.value ? (
                    <div>
                      Set <span className="tw-font-bold">{agent.label}’s goals</span> for activities that are
                      tracked within Brivity. Note that these are automatically logged and do not require
                      manual entry.
                    </div>
                  ) : (
                    "Set goals for activities tracked within Brivity. Note that these are automatically logged and do not require manual entry."
                  )
                }
              />
              <DefaultableGoals
                agent={agent}
                refresh={!wizardIsOpen}
                refreshKey={goalsRefreshKey}
                year={year}
                isAdmin={isAdmin}
                section="activity"
                workingMonths={12}
                title="Logged Activity Goals"
                description={
                  agent?.value ? (
                    <div>
                      Set the <span className="tw-font-bold">activity goal standards for {agent.label}</span>{" "}
                      for manually logged lead generation activities, lead nurturing activities, or custom
                      goals that you want your team to track.
                    </div>
                  ) : (
                    "Track progress in activities related to lead generation, lead nurturing, or any other custom goals you wish to track."
                  )
                }
                customGoals
              />
            </div>
          </div>
          <BusinessPlanSheet />
        </WizardProvider>
      </div>
    </div>
  );
};

ReportingGoals.propTypes = {
  agents: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default ReportingGoals;
